import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { appRoutesLazy } from './route-config-lazy';

// Lazy load Home component to avoid unnecessary renders
const Home = React.lazy(() => import('../../features/home'));

/**
 * Custom hook for mapping routes to components
 * Optimized with memoization to prevent unnecessary re-renders
 */
export const useRouteMapper = () => {
  const { pathname = '' } = useLocation();

  // Memoize the route lookup map for better performance
  const routeMap = useMemo(() => {
    const map = new Map<string, JSX.Element>();

    // Build route map from appRoutesLazy
    appRoutesLazy.forEach((route) => {
      // Add main route
      map.set(`/${route.path}`, route.component);

      // Add sub-routes
      route.elements.forEach((subRoute) => {
        map.set(`/${subRoute.path}`, subRoute.component);
      });
    });

    return map;
  }, []); // Empty dependency array since appRoutesLazy is static

  // Memoize the component resolution to avoid re-creating components on every render
  const currentComponent = useMemo(() => {
    // Find matching component
    const matchedComponent = routeMap.get(pathname);

    if (matchedComponent) {
      return matchedComponent;
    }

    // Default to Home component with Suspense wrapper
    return (
      <React.Suspense fallback={<div>Cargando...</div>}>
        <Home />
      </React.Suspense>
    );
  }, [pathname, routeMap]);

  return currentComponent;
};

export default useRouteMapper;
